import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, Star, ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useContentValue } from "../context/ContentContext";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const { t } = useTranslation();
  const eyebrow = useContentValue("pricing.hero.eyebrow", "[ simple pricing ]");
  const title = useContentValue("pricing.hero.title", "Pagás por lo que usás. Sin suscripción.");
  const sub = useContentValue("pricing.hero.subtitle", "");
  const note = useContentValue("pricing.hero.note", "");

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/"><BrandLogo size="lg" /></Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/features" className="text-muted-foreground hover:text-foreground font-medium">{t("nav.features")}</Link>
            <Link to="/pricing" className="text-foreground font-semibold">{t("nav.pricing")}</Link>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/signup"><Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">{t("cta.get_started")}</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border" data-testid="pricing-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-primary font-bold">{eyebrow}</div>
          <h1 className="mt-4 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-tight">{title}</h1>
          <p className="mt-5 text-lg text-muted-foreground">{sub}</p>
          {note && <p className="mt-4 text-xs font-mono text-muted-foreground">{note}</p>}
        </div>
      </section>

      <PackageCards />
      <WhatTokensEnable />
      <PricingFAQ />

      <section className="bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display font-black tracking-tight text-3xl lg:text-4xl">100 tokens gratis para arrancar</h2>
          <p className="mt-3 text-primary-foreground/80">Sin tarjeta de crédito.</p>
          <div className="mt-6">
            <Link to="/signup"><Button size="lg" variant="secondary" className="rounded-sm h-12 px-8 bg-white text-primary hover:bg-white/90" data-testid="pricing-cta-btn">
              {t("cta.get_started")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PackageCards() {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    // Public-ish: requires auth, so we hit the unauthenticated branding endpoint
    // and a small public packages endpoint instead. For now, use a hardcoded
    // mirror of seed values since /billing/packages requires login.
    setPackages([
      {id:"starter", name:"Starter", tokens:1000, price_usd:15, badge:null},
      {id:"pro",     name:"Pro",     tokens:5000, price_usd:60, badge:"Más popular"},
      {id:"studio",  name:"Studio",  tokens:25000, price_usd:250, badge:"Mejor valor"},
    ]);
  }, []);

  return (
    <section className="border-b border-border" data-testid="package-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}}
              viewport={{once:true}} transition={{duration:0.4, delay:i*0.1}}
              className={`relative rounded-md border p-7 flex flex-col ${
                pkg.badge ? "border-primary bg-primary/5" : "border-border bg-card"
              }`}
              data-testid={`pkg-${pkg.id}`}
            >
              {pkg.badge && (
                <div className="absolute -top-2.5 right-5 inline-flex items-center gap-1 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider rounded-sm">
                  <Star className="h-3 w-3" /> {pkg.badge}
                </div>
              )}
              <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{pkg.name}</div>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="font-display font-black text-4xl tabular-nums">{pkg.tokens.toLocaleString("es-AR")}</span>
                <span className="text-sm font-mono text-muted-foreground">tokens</span>
              </div>
              <div className="mt-1 text-3xl font-display font-bold">USD {pkg.price_usd}</div>
              <div className="text-xs font-mono text-muted-foreground mt-1">USD {(pkg.price_usd / pkg.tokens * 1000).toFixed(2)} por 1.000 tokens</div>
              <Link to="/signup" className="mt-6">
                <Button className={`rounded-sm w-full ${pkg.badge ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`} variant={pkg.badge ? "default" : "outline"} data-testid={`pkg-cta-${pkg.id}`}>
                  Empezar con {pkg.name}
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatTokensEnable() {
  const title = useContentValue("pricing.what.title", "¿Qué podés hacer con tus tokens?");
  const sub = useContentValue("pricing.what.subtitle", "");
  const rows = [
    {label: "Chat con IA", note: "consulta promedio", cost: "≈ 5 tokens"},
    {label: "Análisis de PDF", note: "10 páginas", cost: "≈ 20 tokens"},
    {label: "Resumen automático", note: "por archivo", cost: "≈ 8 tokens"},
    {label: "Modo voz", note: "por minuto", cost: "≈ 30 tokens"},
    {label: "Transcripción con diarización", note: "post-llamada", cost: "≈ 25 tokens"},
    {label: "Embedding de documento", note: "para RAG", cost: "≈ 1 token / chunk"},
  ];
  return (
    <section className="border-b border-border bg-muted/20" data-testid="what-tokens-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-center">
          <h2 className="font-display font-black tracking-tight text-3xl lg:text-4xl">{title}</h2>
          <p className="mt-3 text-muted-foreground">{sub}</p>
        </div>
        <div className="mt-10 border border-border rounded-md overflow-hidden bg-card">
          {rows.map((r, i) => (
            <div key={i} className={`grid grid-cols-[1fr_auto] gap-4 px-5 py-4 ${i > 0 ? "border-t border-border" : ""}`}>
              <div>
                <div className="font-semibold">{r.label}</div>
                <div className="text-xs font-mono text-muted-foreground">{r.note}</div>
              </div>
              <div className="font-mono tabular-nums text-primary font-bold self-center">{r.cost}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingFAQ() {
  const title = useContentValue("pricing.faq.title", "Preguntas sobre facturación");
  const q1 = useContentValue("pricing.faq.q1.q", "");
  const a1 = useContentValue("pricing.faq.q1.a", "");
  const q2 = useContentValue("pricing.faq.q2.q", "");
  const a2 = useContentValue("pricing.faq.q2.a", "");
  const q3 = useContentValue("pricing.faq.q3.q", "");
  const a3 = useContentValue("pricing.faq.q3.a", "");
  const items = [
    {q: q1, a: a1}, {q: q2, a: a2}, {q: q3, a: a3},
  ].filter(x => x.q);
  const [open, setOpen] = useState(null);
  return (
    <section className="border-b border-border">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
        <h2 className="font-display font-black tracking-tight text-3xl text-center">{title}</h2>
        <div className="mt-8 space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-border rounded-md bg-card overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-muted/30 transition">
                <span className="font-semibold">{it.q}</span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{it.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
